import type { i18n as I18nInstance } from 'i18next';

type JsonLoader = () => Promise<{ default: Record<string, unknown> }>;

// One literal import() per file so webpack splits each language into its own
// chunk - a templated path like `./json/${lang}.json` would make webpack bundle
// every language into a single chunk instead of loading just the one needed.
// `se` is intentionally mapped to the `sv` json files - that mismatch between
// LangCode.se and the sv.json filename predates this change (see languages.ts).
const loaders: Record<string, [JsonLoader, JsonLoader, JsonLoader]> = {
  da: [() => import('./json/da.json'), () => import('./json/badges/da.json'), () => import('./json/tutorials/da.json')],
  de: [() => import('./json/de.json'), () => import('./json/badges/de.json'), () => import('./json/tutorials/de.json')],
  nl: [() => import('./json/nl.json'), () => import('./json/badges/nl.json'), () => import('./json/tutorials/nl.json')],
  fi: [() => import('./json/fi.json'), () => import('./json/badges/fi.json'), () => import('./json/tutorials/fi.json')],
  se: [() => import('./json/sv.json'), () => import('./json/badges/sv.json'), () => import('./json/tutorials/sv.json')],
  no: [() => import('./json/no.json'), () => import('./json/badges/no.json'), () => import('./json/tutorials/no.json')],
  fr: [() => import('./json/fr.json'), () => import('./json/badges/fr.json'), () => import('./json/tutorials/fr.json')],
  es: [() => import('./json/es.json'), () => import('./json/badges/es.json'), () => import('./json/tutorials/es.json')],
  it: [() => import('./json/it.json'), () => import('./json/badges/it.json'), () => import('./json/tutorials/it.json')],
  pt: [() => import('./json/pt.json'), () => import('./json/badges/pt.json'), () => import('./json/tutorials/pt.json')],
  tr: [() => import('./json/tr.json'), () => import('./json/badges/tr.json'), () => import('./json/tutorials/tr.json')],
  is: [() => import('./json/is.json'), () => import('./json/badges/is.json'), () => import('./json/tutorials/is.json')],
};

// `en` is already in the i18next instance's resources at init (see
// i18next-factory.ts) - calling this for it would just be a no-op lookup.
export async function ensureLanguageLoaded(instance: I18nInstance, langCode: string): Promise<void> {
  if (langCode === 'en' || instance.hasResourceBundle(langCode, 'translation')) return;

  const fileLoaders = loaders[langCode];
  if (!fileLoaders) return;

  const [translation, badges, tutorials] = await Promise.all(fileLoaders.map((load) => load()));
  instance.addResourceBundle(
    langCode,
    'translation',
    Object.assign({}, translation.default, badges.default, tutorials.default),
    true,
    true,
  );
}
